import { RequestStatusEnum } from "@/lib/enum/request-status-enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IVSelectItem } from "@/lib/interfaces/v-select-item.interface";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { PageFilterOperationEnum } from "@/lib/utility/data/page-filter-operation.enum";
import { GoToHelper } from "@/lib/utility/goToHelper";
import { log } from "@/lib/utility/log";
import { $t } from "@/lib/utility/t";
import { PageFilterElement } from "@/models/page-filter-element.entity";
import sharedContentService from "@/services/mrfiktiv/services/sharedContentService";
import snapshotSignRequestService from "@/services/mrfiktiv/services/snapshot-sign-request.service";
import {
  MrfiktivSnapshotSignerViewModelGen,
  MrfiktivSnapshotSignRequestViewModelGen,
  MrfiktivStatusHistoryElementViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { HandoverDataAccessLayer } from "@/store/modules/access-layers/handover.access-layer";
import { SnapshotSignRequestDataAccessLayer } from "@/store/modules/access-layers/snapshot-sign-request.access-layer";
import { HandoverSnapshotDataAccessLayer } from "@/store/modules/access-layers/snapshot.access-layer";
import VueRouter from "vue-router";
import { ISharedContent, SharedContent } from "./shared-content.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { SharedContentModule } from "@/store/modules/shared-content.store";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { PartnerUserAccessLayer } from "@/store/modules/access-layers/partner-user.access-layer";

@IsFilterable
export class SnapshotSignRequestBase implements IEntity<ISnapshotSignRequest>, MrfiktivSnapshotSignRequestViewModelGen {
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.id"
  })
  id: string;

  partnerId: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.snapshot.id",
    config: {
      itemCallback: () => HandoverSnapshotDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-snapshot"
    }
  })
  snapshotId: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.handover.id",
    config: {
      itemCallback: () => HandoverDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-handover",
      refType: BackendResourceEnum.HANDOVER
    }
  })
  handoverId: string;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "common.nouns.status",
    config: {
      items: Object.values(RequestStatusEnum).map((status) => ({
        value: status,
        text: `enums.RequestStatusEnum.${status}`
      })) as IVSelectItem[],
      itemValue: "value"
    }
  })
  status: RequestStatusEnum;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.ticket.userId",
    config: {
      itemCallback: () => PartnerUserAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      itemText: "fullName",
      component: "refs-user",
      refType: BackendResourceEnum.USER
    }
  })
  userId: string;

  signer?: MrfiktivSnapshotSignerViewModelGen;

  @FilterConfig({ type: Timestamp })
  timestamp: ITimestamp;

  history: MrfiktivStatusHistoryElementViewModelGen[];

  isLoadingSharedDocument = false;

  sharedDocument?: ISharedContent;

  constructor(obj?: Partial<MrfiktivSnapshotSignRequestViewModelGen | ISnapshotSignRequest>) {
    this.id = obj?.id ?? "";
    this.partnerId = obj?.partnerId ?? "";
    this.handoverId = obj?.handoverId ?? "";
    this.snapshotId = obj?.snapshotId ?? "";
    this.status = (obj?.status as RequestStatusEnum) ?? RequestStatusEnum.DRAFT;
    this.userId = obj?.userId ?? "";
    this.signer = obj?.signer;
    this.timestamp = new Timestamp(obj?.timestamp);
    this.history = obj?.history ?? [];
  }

  map(vm?: MrfiktivSnapshotSignRequestViewModelGen) {
    if (!vm) return;
    this.id = vm.id;
    this.partnerId = vm.partnerId;
    this.handoverId = vm.handoverId;
    this.snapshotId = vm.snapshotId;
    this.status = vm.status as RequestStatusEnum;
    this.userId = vm.userId;
    this.signer = vm.signer;
    this.timestamp = new Timestamp(vm.timestamp);
    this.history = vm.history ?? [];
  }

  /**
   * Colors - based on @see SIGN_COLOR_BY_STATUS
   */
  static get statusColorMap() {
    return new Map([
      [RequestStatusEnum.DRAFT, "grey"],
      [RequestStatusEnum.REQUESTED, "primary"],
      [RequestStatusEnum.REPLIED, "green lighten-2"],
      [RequestStatusEnum.ACCEPTED, "green"],
      [RequestStatusEnum.REJECTED_BY_REQUESTED, "red"],
      [RequestStatusEnum.REJECTED_BY_REQUESTING, "red"],
      [RequestStatusEnum.REQUEST_MORE_DATA, "orange"],
      [RequestStatusEnum.FINISHED, "deep-purple lighten-1"]
    ]);
  }

  static get statusIconMap() {
    return new Map([
      [RequestStatusEnum.DRAFT, "mdi-file-document-outline"],
      [RequestStatusEnum.REQUESTED, "mdi-file-document-outline"],
      [RequestStatusEnum.REPLIED, "mdi-file-document-outline"],
      [RequestStatusEnum.REQUEST_MORE_DATA, "mdi-message-question-outline"],
      [RequestStatusEnum.ACCEPTED, "mdi-check-circle-outline"],
      [RequestStatusEnum.REJECTED_BY_REQUESTED, "mdi-close-circle-outline"],
      [RequestStatusEnum.REJECTED_BY_REQUESTING, "mdi-close-circle-outline"],
      [RequestStatusEnum.FINISHED, "mdi-check-bold"]
    ]);
  }

  get statusColor() {
    return SnapshotSignRequestBase.statusColorMap.get(this.status) || "grey";
  }

  get statusIcon() {
    return SnapshotSignRequestBase.statusIconMap.get(this.status) || "mdi-file-document-outline";
  }

  get statusReadable() {
    return $t(`enums.RequestStatusEnum.${this.status}`);
  }

  async fetch(): Promise<this> {
    const res = await snapshotSignRequestService.getOne(this.partnerId, this.id);

    this.map(res);
    SnapshotSignRequestDataAccessLayer.set(this);
    return this;
  }

  async fetchSharedDocument(): Promise<this> {
    this.isLoadingSharedDocument = true;
    try {
      const page = await sharedContentService.getSharedContentPaginated({
        partnerId: this.partnerId,
        filter: [
          new PageFilterElement({ key: "refs.refId", operation: PageFilterOperationEnum.EQUAL, value: this.id })
            .dtoPagination
        ]
      });
      const first = page?.data?.[0];
      const shared = first ? new SharedContent(first) : null;
      this.sharedDocument = shared || undefined;
    } catch (e) {
      log.error(e);
    } finally {
      this.isLoadingSharedDocument = false;
    }
    return this;
  }

  async create(): Promise<this> {
    const res = await snapshotSignRequestService.create(this.partnerId, {
      snapshotId: this.snapshotId,
      handoverId: this.handoverId,
      status: this.status
    });

    this.map(res);
    SnapshotSignRequestDataAccessLayer.set(this);
    return this;
  }

  async delete(): Promise<void> {
    await snapshotSignRequestService.remove(this.partnerId, this.id);
    SnapshotSignRequestDataAccessLayer.delete(this);
    await SharedContentModule.onRefDelete({ refId: this.id, partnerId: this.partnerId }).catch(log.error);
  }

  goTo(router: VueRouter) {
    return { detail: () => new GoToHelper(router).goToSnapshotDetailView(this.partnerId, this.handoverId, this.id) };
  }
}

type ISnapshotSignRequest = SnapshotSignRequestBase;
const SnapshotSignRequest = Filter.createForClass(SnapshotSignRequestBase);

export { ISnapshotSignRequest, SnapshotSignRequest };
